// src/pages/DashboardPage.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList } from 'recharts';
import DashboardDataTab from '../components/DashboardDataTab';
import { Document, Packer, Paragraph, TextRun } from 'docx';

export default function DashboardPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    dashboardData: initialDashboardData,
    dashboardCodebook: initialDashboardCodebook,
    projectID,
    codeName,
    dataCount,
    question,
    masterExist,
    AI_Report,
    codeBookLibrary: initialCodeBookLibrary
  } = location.state || {};

  useEffect(() => {
    if (!location.state || !projectID) {
      navigate('/ProjectsPage');
    }
  }, [location.state, projectID, navigate]);

  // Yerel state tanımlamaları
  const [tabValue, setTabValue] = useState(0);
  const [localDashboardData, setLocalDashboardData] = useState(initialDashboardData);
  const [localDashboardCodebook, setLocalDashboardCodebook] = useState(initialDashboardCodebook);
  const [editableAIText, setEditableAIText] = useState(AI_Report || '');
  const [isLoading, setIsLoading] = useState(false);
  const [askAIQuestion, setAskAIQuestion] = useState('');
  const [editableAIStoryText, setEditableAIStoryText] = useState('');
  const [isLoadingStory, setIsLoadingStory] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [codeBookLibrary, setCodeBookLibrary] = useState(initialCodeBookLibrary || { codebooks: [] });

  const handleTabChange = (event, newValue) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        t('dashboard.unsavedSwitch') ||
        "You have unsaved changes. Do you want to discard them and switch tabs?"
      );
      if (!confirmed) {
        return;
      } else {
        setHasUnsavedChanges(false);
      }
    }
    setTabValue(newValue);
  };
  
  // ortak helper
  async function postJSON(url, payload) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',          // gereksiz cookie gönderme
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      // Sunucu hata gövdesi JSON ise açıp aktaralım
      let msg = `HTTP ${res.status}`;
      try { msg = (await res.json()).error || msg; } catch (_) {}
      throw new Error(msg);
    }
    return res.json();              // başarıyla parse edilmiş JSON döner
  }

  // handleSaveChanges
  const handleSaveChanges = async (updatedRowData, updatedCodebook) => {
    try {
      setIsSaving(true);

      const payload = {
        projectID,
        codeName,
        codebook: updatedCodebook,
        result_json: updatedRowData
      };

      // postJSON artık doğrudan sunucudan gelen JSON'u döndürür
      const saveResult = await postJSON(
        'https://codini-project-dashboard-save-update-795950092325.europe-west3.run.app',
        payload
      );

      // İsterseniz sunucu yanıtındaki alanları kullanabilirsiniz
      console.log('saveResult:', saveResult);

      alert(t('dashboard.saveSuccess') || "Data saved successfully!");
      setHasUnsavedChanges(false);
      setLocalDashboardData(updatedRowData);
      setLocalDashboardCodebook(updatedCodebook);

    } catch (error) {
      console.error(t('dashboard.saveError'), error);
      alert(`${t('dashboard.saveFailure') || "The data could not be saved"}\n${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnsavedChanges = useCallback((isChanged) => {
    setHasUnsavedChanges(isChanged);
  }, []);

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        t('dashboard.unsavedCancel') ||
        "You have unsaved changes. Do you want to discard them and cancel?"
      );
      if (confirmed) {
        setHasUnsavedChanges(false);
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const getResultFile = () => {
    setIsDownloading(true);
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);

    fetch("https://codini-project-4-7kpxqstxwq-ey.a.run.app", {
      method: "POST",
      body: formData
    })
      .then(response => {
        if (!response.ok) throw new Error(t('dashboard.downloadError') || "Network response was not ok");
        return response.json();
      })
      .then(data => {
        setIsDownloading(false);
        if (data.signed_url) {
          const signedUrl = data.signed_url;
          const downloadLink = document.createElement('a');
          downloadLink.href = signedUrl;
          downloadLink.download = 'Result.xlsx';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        } else {
          alert(t('dashboard.noSignedURL') || "Signed URL not found in the response");
        }
      })
      .catch(error => {
        console.error('Error:', error);
        alert(t('dashboard.downloadError') || "Failed to retrieve the public URL");
        setIsDownloading(false);
      });
  };

  // Chart hesaplamaları
  const categoryOptions = localDashboardCodebook ? localDashboardCodebook.map(item => item.categoryName) : [];
  const categoryCounts = {};
  categoryOptions.forEach(category => {
    categoryCounts[category] = localDashboardData.filter(item =>
      Object.keys(item).some(key => key.startsWith("Category") && item[key] === category)
    ).length;
  });
  const totalCount = localDashboardData.length;
  const sortedCategories = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]);
  const categoryChartData = sortedCategories.map(([category, count]) => ({
    name: category,
    percentage: parseFloat(((count / totalCount) * 100).toFixed(2))
  }));

  const sentimentCounts = sortedCategories.reduce((acc, [category]) => {
    acc[category] = { positive: 0, negative: 0, neutral: 0 };
    localDashboardData.forEach(item => {
      if (Object.keys(item).some(key => key.startsWith("Category") && item[key] === category)) {
        const sentiment = (item.Sentiment || '').toLowerCase();
        if (['positive', 'negative', 'neutral'].includes(sentiment)) {
          acc[category][sentiment] += 1;
        }
      }
    });
    return acc;
  }, {});

  const sentimentChartData = sortedCategories.map(([category]) => {
    const counts = sentimentCounts[category];
    const totalSentiment = counts.positive + counts.negative + counts.neutral;
    return {
      name: category,
      positive: totalSentiment ? parseFloat(((counts.positive / totalSentiment) * 100).toFixed(2)) : 0,
      negative: totalSentiment ? parseFloat(((counts.negative / totalSentiment) * 100).toFixed(2)) : 0,
      neutral: totalSentiment ? parseFloat(((counts.neutral / totalSentiment) * 100).toFixed(2)) : 0,
    };
  });

  // AI Report ve AI Story Assistant fonksiyonları
  const sendAIReport = async () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);

    try {
      const response = await fetch("https://codini-project-aireporting-v2-7kpxqstxwq-ey.a.run.app", {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      if (data.error === "too_much_codebook") {
        alert(t('dashboard.codebookTooMany') || "Codebook has more than 50 categories");
      } else {
        setEditableAIText(data.AI_Report);
      }
    } catch (error) {
      console.error('Error:', error);
      alert(t('dashboard.aiReportError') || "Failed to retrieve the AI Report");
    } finally {
      setIsLoading(false);
    }
  };

  const sendAIQuestion = async () => {
    if (!askAIQuestion.trim()) {
      alert(t('dashboard.fillQuestion') || "Please fill in the question field.");
      return;
    }
    setIsLoadingStory(true);
    const formData = new FormData();
    formData.append('projectID', projectID);
    formData.append('codeName', codeName);
    formData.append('AiQuestion', askAIQuestion);

    try {
      const response = await fetch("https://codini-project-aiquestion-v2-7kpxqstxwq-ey.a.run.app", {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      setEditableAIStoryText(data.AI_question);
      setAskAIQuestion('');
    } catch (error) {
      console.error('Error:', error);
      alert(t('dashboard.aiStoryError') || "Failed to retrieve the AI Story");
    } finally {
      setIsLoadingStory(false);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(editableAIText);
    alert(t('dashboard.copiedToClipboard') || "Text copied to clipboard");
  };

  const handleCopyToClipboardStory = () => {
    navigator.clipboard.writeText(editableAIStoryText);
    alert(t('dashboard.copiedToClipboard') || "Text copied to clipboard");
  };

  const handleExportToDOCX = () => {
    if (!editableAIText) {
      alert(t('dashboard.noTextExport') || "No text to export");
      return;
    }
  
    // Metni satırlara ayır
    const lines = editableAIText.split(/\r?\n/);
    const paragraphs = [];
  
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine === '') {
        return; // Boş satırları atla
      }
  
      let children = [];
  
      if (trimmedLine.startsWith('### ')) {
        // Başlık: ### ile başlayan satırları kalın yap
        const headingText = trimmedLine.replace('### ', '');
        children.push(new TextRun({ text: headingText, bold: true }));
        paragraphs.push(new Paragraph({ children }));
      } else if (trimmedLine.startsWith('- ')) {
        // Madde işareti: - ile başlayan satırları bullet list yap
        const bulletText = trimmedLine.replace('- ', '');
        const parts = bulletText.split('**');
        parts.forEach((part, index) => {
          if (index % 2 === 0) {
            children.push(new TextRun(part));
          } else {
            children.push(new TextRun({ text: part, bold: true }));
          }
        });
        paragraphs.push(new Paragraph({ children, bullet: { level: 0 } }));
      } else {
        // Normal satır: ** ile belirtilen bölümleri kalın yap
        const parts = trimmedLine.split('**');
        parts.forEach((part, index) => {
          if (index % 2 === 0) {
            children.push(new TextRun(part));
          } else {
            children.push(new TextRun({ text: part, bold: true }));
          }
        });
        paragraphs.push(new Paragraph({ children }));
      }
    });
  
    // DOCX dosyasını oluştur
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: paragraphs,
        },
      ],
    });
  
    // Dosyayı indir
    Packer.toBlob(doc)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'AI_Report.docx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Error exporting to DOCX:', error);
        alert(t('dashboard.exportError') || "Failed to export the document");
      });
  };
  
  const handleExportToDOCXStory = () => {
    if (!editableAIStoryText) {
      alert(t('dashboard.noTextExport') || "No text to export");
      return;
    }
  
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            new Paragraph({
              children: [
                new TextRun(editableAIStoryText)
              ],
            }),
          ],
        },
      ],
    });
  
    Packer.toBlob(doc).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AI_Story.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }).catch((error) => {
      console.error('Error exporting to DOCX:', error);
      alert(t('dashboard.exportError') || "Failed to export the document");
    });
  };

  // Chart indirme fonksiyonları
  const downloadSVGAsJPEG = (svgElement, fileName) => {
    const svgString = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height); 
      ctx.drawImage(img, 0, 0);
      const jpegUrl = canvas.toDataURL('image/jpeg');
      const link = document.createElement('a');
      link.download = fileName;
      link.href = jpegUrl;
      link.click();
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const downloadChart = (chartClass, fileName) => {
    const chartContainer = document.querySelector(`.${chartClass} .recharts-wrapper`);
    if (chartContainer) {
      const svgElement = chartContainer.querySelector('svg');
      if (svgElement) {
        downloadSVGAsJPEG(svgElement, fileName);
      } else {
        alert(t('dashboard.noSVG') || "SVG element not found within the chart container");
      }
    } else {
      alert(t('dashboard.noChartContainer') || "Chart container not found");
    }
  };

  const categoryChartHeight = Math.max(400, 50 * categoryChartData.length);
  const sentimentChartHeight = Math.max(400, 50 * sentimentChartData.length);

  return (
    <Box sx={{
      backgroundColor: '#f4f4f4',
      minHeight: '100vh',
      padding: { xs: '10px', sm: '2vw', md: '3vw' }, // Dynamic padding based on screen size
      width: '100vw', // Full viewport width
      boxSizing: 'border-box',
    }}>
      <Box
        sx={{
          width: '94%', // Slightly less than full width to allow padding
          maxWidth: 'none', // Remove max-width constraint
          margin: '0 auto', // Center the content
          backgroundColor: '#fff',
          borderRadius: 2,
          padding: { xs: '10px', sm: '15px', md: '20px' }, // Responsive internal padding
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)', // Subtle shadow for depth
        }}
      >
        {isSaving && (
          <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSaving}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <Box sx={{ mb: 2, mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="contained" onClick={getResultFile} disabled={isDownloading}>
            {isDownloading
              ? t('dashboard.downloading') || "Downloading..."
              : t('dashboard.downloadResult') || "Download Result File"}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/ProjectsPage')}>
            {t('cancel') || 'İptal'}
          </Button>
        </Box>
        <Paper elevation={3} sx={{ p: 2, mb: 3, background: '#fed9b7' }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {t('dashboard.title') || "Dashboard"}
          </Typography>
          <Typography variant="body1">
            {t('question') || "Question"}: {question}
          </Typography>
          <Typography variant="body1">
            {t('dataCount') || "Data Count"}: {dataCount}
          </Typography>
        </Paper>

        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label={t('dashboard.categoryChart') || "Category Chart"} />
          <Tab label={t('dashboard.sentimentChart') || "Sentiment Chart"} />
          <Tab label={t('dashboard.table') || "Table"} />
          <Tab label={t('dashboard.aiReport') || "AI Report"} />
          <Tab label={t('dashboard.aiStoryAssistant') || "AI Story Assistant"} />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ mt: 3, mb: 2 }}>     
            <Box sx={{ height: categoryChartHeight }} className="category-chart">  
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryChartData} layout="vertical" margin={{ top: 20, right: 20, left: 60, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(tick) => tick + '%'} />
                  <YAxis dataKey="name" type="category" width={200} tick={{ fontSize: 12 }} interval={0} />
                  <Tooltip formatter={(value) => value.toFixed(2) + '%'} />
                  <Legend />
                  <Bar dataKey="percentage" fill="rgba(54, 162, 235, 0.7)" radius={[0, 10, 10, 0]} label={{ position: 'right', fill: "#666", fontSize: 12, formatter: (value) => `${value.toFixed(2)}%` }} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
              <Button variant="contained" onClick={() => downloadChart('category-chart', 'category_chart.jpg')}>
                {t('dashboard.downloadChart') || "Download Chart"}
              </Button> 
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Box sx={{ height: sentimentChartHeight }} className="sentiment-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sentimentChartData} layout="vertical" margin={{ top: 20, right: 20, left: 60, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={(tick) => tick + '%'} ticks={[0, 25, 50, 75, 100]} />
                  <YAxis dataKey="name" type="category" width={200} tick={{ fontSize: 12 }} interval={0} tickFormatter={(value) => value.length > 30 ? value.substring(0, 27) + '...' : value} />
                  <Tooltip formatter={(value) => value.toFixed(2) + '%'} />
                  <Legend />
                  <Bar dataKey="positive" stackId="a" fill="rgba(75, 192, 192, 0.7)">
                    <LabelList dataKey="positive" position="insideLeft" fill="black" fontSize="12" formatter={(value) => value === 0 ? '' : `${value.toFixed(2)}%`} />
                  </Bar>
                  <Bar dataKey="neutral" stackId="a" fill="rgba(255, 206, 86, 0.7)">
                    <LabelList dataKey="neutral" position="insideLeft" fill="black" fontSize="12" formatter={(value) => value === 0 ? '' : `${value.toFixed(2)}%`} />
                  </Bar>
                  <Bar dataKey="negative" stackId="a" fill="rgba(255, 99, 132, 0.7)">
                    <LabelList dataKey="negative" position="insideLeft" fill="black" fontSize="12" formatter={(value) => value === 0 ? '' : `${value.toFixed(2)}%`} />
                  </Bar>
                  <Bar dataKey="total" stackId="a" fill="transparent">
                    <LabelList dataKey="total" position="center" fill="black" formatter={(value) => `${value}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
              <Button variant="contained" onClick={() => downloadChart('sentiment-chart', 'sentiment_chart.jpg')}>
                {t('dashboard.downloadChart') || "Download Chart"}
              </Button>
            </Box>
          </Box>
        )}

        {tabValue === 2 && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <DashboardDataTab
              data={localDashboardData}
              dashboardCodebook={localDashboardCodebook}
              onSaveChanges={handleSaveChanges}
              onDataChanged={handleUnsavedChanges}
              projectID={projectID}
              codeName={codeName}
              codeBookLibrary={codeBookLibrary}
              onUpdateCodeBookLibrary={setCodeBookLibrary}
            />
          </Box>
        )}

        {tabValue === 3 && (
          <Box sx={{ mt: 3, mb: 2 }}>
            {editableAIText === "none" ? (
              <Button variant="contained" color="error" onClick={sendAIReport} disabled={isLoading}>
                {isLoading
                  ? t('dashboard.loading') || "Loading..."
                  : t('dashboard.createAISummary') || "Create AI Summary"}
              </Button>
            ) : (
              <Box>
                <TextField
                  multiline
                  fullWidth
                  minRows={6}
                  value={editableAIText}
                  onChange={(e) => setEditableAIText(e.target.value)}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button variant="contained" color="primary" onClick={handleCopyToClipboard}>
                    {t('dashboard.copyToClipboard') || "Copy to Clipboard"}
                  </Button>
                  <Button variant="contained" color="success" onClick={handleExportToDOCX}>
                    {t('dashboard.saveAsDocx') || "Save As Docx"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {tabValue === 4 && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                placeholder={t('dashboard.askQuestion') || "Ask a question"}
                value={askAIQuestion}
                onChange={(e) => setAskAIQuestion(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={sendAIQuestion}
                disabled={isLoadingStory || !askAIQuestion.trim()}
              >
                {isLoadingStory
                  ? t('dashboard.asking') || "Asking..."
                  : t('dashboard.ask') || "Ask"}
              </Button>
            </Box>
            {editableAIStoryText && (
              <Box>
                <TextField
                  multiline
                  fullWidth
                  minRows={6}
                  value={editableAIStoryText}
                  onChange={(e) => setEditableAIStoryText(e.target.value)}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button variant="contained" color="primary" onClick={handleCopyToClipboardStory}>
                    {t('dashboard.copyToClipboard') || "Copy to Clipboard"}
                  </Button>
                  <Button variant="contained" color="success" onClick={handleExportToDOCXStory}>
                    {t('dashboard.saveAsDocx') || "Save As Docx"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}